//#region    ////////// packages
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useState, useEffect } from 'react';
//#endregion ////////// packages

//#region    ////////// components
import FloatingLabelInput from '@/shared/components/inputs/FloatingLabelInput';
import AuthLayout from '@/pages/auth/_components/AuthLayout';
import IconCircleInfo from '@/shared/assets/svg/common/icon-circle-info.svg?react';
import IconCircleCheck from '@/shared/assets/svg/common/icon-circle-check.svg?react';
//#endregion ////////// components

//#region    ////////// utils & hooks
import BgSignin from '@/shared/assets/img/bg-signin.jpg';
import { getAuth } from '@/lib/api/generated/auth/auth';
import { useAuthStore } from '@/shared/auth/authStore';
import { PATH, HOME_PATH_BY_ROLE } from '@/shared/constants';
//#endregion ////////// utils & hooks

const { authConfirmPasswordReset } = getAuth();

interface FormValues {
  RHF_newPassword: string;
  RHF_confirmPassword: string;
}

export default function ConfirmPasswordResetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const userInfo = useAuthStore((state) => state.userInfo);
  const isLoggedIn = userInfo.userRole !== '';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { RHF_newPassword: '', RHF_confirmPassword: '' },
  });

  const [parentRef] = useAutoAnimate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bannerInfo, setBannerInfo] = useState<{ type: 'error' | 'success'; children: React.ReactNode } | null>(null);

  useEffect(() => {
    if (!token) navigate(PATH.AUTH.RESET_PASSWORD, { replace: true });
  }, [token, navigate]);

  const onSubmit = async (data: FormValues) => {
    if (!token) return;
    setIsSubmitting(true);
    try {
      await authConfirmPasswordReset({ token, newPassword: data.RHF_newPassword });
      setBannerInfo({
        type: 'success',
        children: <p className='text-sm font-semibold'>비밀번호가 성공적으로 재설정되었습니다.</p>,
      });
      setTimeout(() => {
        if (isLoggedIn) {
          const homePath = HOME_PATH_BY_ROLE[userInfo.userRole] ?? PATH.AUTH.SIGNIN;
          navigate(homePath, { replace: true });
        } else {
          navigate(PATH.AUTH.SIGNIN, { replace: true });
        }
      }, 1500);
    } catch {
      setBannerInfo({
        type: 'error',
        children: (
          <p className='text-sm'>
            <span className='font-semibold'>비밀번호 재설정에 실패했습니다.</span>
            <br />
            <span className='font-medium'>링크가 만료되었거나 유효하지 않습니다. 다시 요청해주세요.</span>
          </p>
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout imgUrl={BgSignin}>
      <div>
        <h3 className='text-basic-black text-d3xl font-semibold whitespace-pre-line'>New password</h3>
        <p className='mt-1 text-sm text-gray-900'>Enter your new password below.</p>

        <div className='mt-8' ref={parentRef}>
          {bannerInfo && <FeedbackBanner type={bannerInfo.type}>{bannerInfo.children}</FeedbackBanner>}

          <form onSubmit={handleSubmit(onSubmit)} className='mt-3 flex flex-col gap-5'>
            <div className='flex flex-col gap-4'>
              <FloatingLabelInput
                {...register('RHF_newPassword', {
                  required: 'New password is required.',
                  minLength: { value: 8, message: 'Password must be at least 8 characters.' },
                })}
                type='password'
                error={errors.RHF_newPassword?.message}
                label='New Password'
                required
              />
              <FloatingLabelInput
                {...register('RHF_confirmPassword', {
                  required: 'Please confirm your password.',
                  validate: (value) => value === watch('RHF_newPassword') || 'Passwords do not match.',
                })}
                type='password'
                error={errors.RHF_confirmPassword?.message}
                label='Confirm Password'
                required
              />
            </div>
            <button
              type='submit'
              disabled={isSubmitting}
              className='text-basic-white h-14 w-full rounded-lg bg-blue-400 text-base font-semibold disabled:opacity-60'>
              {isSubmitting ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}

interface FeedbackBannerProps {
  type: 'success' | 'error';
  children: React.ReactNode;
}

const bannerConfig = {
  success: {
    icon: (
      <IconCircleCheck style={{ '--icon-bg': 'var(--support-green)', '--icon-fg': 'var(--color-basic-white)' } as React.CSSProperties} className='h-5 w-5' />
    ),
    bg: 'bg-support-green-shade text-support-green',
  },
  error: {
    icon: <IconCircleInfo style={{ '--icon-bg': 'var(--support-red)', '--icon-fg': 'var(--color-basic-white)' } as React.CSSProperties} className='h-5 w-5' />,
    bg: 'bg-support-red-shade text-support-red',
  },
};

function FeedbackBanner({ type, children }: FeedbackBannerProps) {
  return (
    <div className={`mt-8 flex items-center gap-3 rounded-lg p-3 text-sm ${bannerConfig[type]?.bg ?? ''}`}>
      {bannerConfig[type]?.icon}
      <div>{children}</div>
    </div>
  );
}
